/**
 * Vue Store that holds data of the current Project.
 *
 * @author Documill 2024
 */

import apiCall from './../../utils/api';
import commons from './../../utils/commons';
import tasks from './../../utils/tasks';
import { logger } from '../../utils/logger';
import debounce from 'lodash/debounce';

export default {

  state: {
    /**
     * The data of a Project that is being accessed by user.
     *
     * Sign only project should have only one anchor and in this
     * anchor there is one single sign phase followed by a a signle delivery phase.
     *
     */
    project: {},

    /**
     * Project document annotations.
     */
    annotations:[],

    scheduledProjectReloadCanceled: null,
    scheduledProjectReload:null,
  },


  getters: {

    scheduledProjectReload(state) {
      return state.scheduledProjectReload;
    },

    scheduledProjectReloadCanceled(state) {
      return state.scheduledProjectReloadCanceled;
    },

    getAnnotations(state) {
      return state.annotations;
    },

    project(state) {
      return state.project;
    },

    projectId(state) {
      return state.project.id;
    },

    projectStatus: (state) =>{
      return state.project.projectStatus;
    },

    document: (state) => {
      if(state.project.anchors.length > 1)
        logger.warn("Project has more than one anchor");

      return state.project.anchors[0].anchorDocument;
    },

    signerTasks: (state) => {
      return state.project.tasks.filter(task => task.taskRole === 'SIGNER')
    },

    signConfigurationTasks: (state) => {
      return state.project.tasks.filter(task => task.taskRole === 'SIGNING_CONFIGURATOR')
    },

    recipientTasks: (state) => {
      return state.project.tasks.filter(task => task.taskRole === 'INTERNAL_READER');
    },

    signSteps: (state) => {
      return state.project.anchors[0].steps.filter(step => step.goal === 'SIGN');
    },

    deliverySteps: (state) => {
      return state.project.anchors[0].steps.filter(step => step.goal === 'DELIVER');
    },

    signPhases: (state) => {
      return state.project.phases.filter(phase => phase.phaseType === 'SIGNING');
    },

    signPhaseId: (state,getters) => {
      let signPhases = getters.signPhases;

      if(signPhases.length > 0)
        return signPhases[0].id;
    },

    anchor :(state) => {
      return state.project.anchors[0];
    },

    anchorId: (state) => {
      if(state.project.anchors.length > 0)
        return state.project.anchors[0].id;
    },

    signingOrder: (state) => {
      return state.project.signingOrder;
    },

    /**
     * Returns the collaborators in the current project.
     */
    getProjectCollaborators: (state) => {
      return state.project.collaborators != null ?
             state.project.collaborators :
             [];
    },

     /**
     * Gets collaborator by identity.
     */
     getCollaboratorById: (state) => collaboratorId => {
      return state.project.collaborators != null ?
             state.project.collaborators.find(collaborator => collaborator.id === collaboratorId) :
             null;
    },

     /**
     * Gets Collaborator by email.
     */
     getCollaboratorByUserEmail: (state) => email => {
      return state.project.collaborators != null ?
             state.project.collaborators.find(collaborator => collaborator.email === email):
             null;
    },

    /**
     * Gets Collaborator object by organization user identity.
     */
    getCollaboratorByUserId: (state) => userId => {
      return state.project.collaborators != null ?
             state.project.collaborators.find(collaborator => collaborator.userId === userId) :
             null;
    },


    getStepById: (state) => stepId => {
      let step;
      state.project.anchors.forEach((anchor) => {
        let stepInAnchor = anchor.steps.find(step => step.id === stepId);
        if (stepInAnchor) {
          step = stepInAnchor;
        }
      });

      return step;
    },

    getTaskById: (state,getters) => taskId => {
      let task = state.project.tasks.find(task => task.id === taskId);
      return task;
    },

    getAnchorById: (state) => anchorId => {
      return state.project.anchors.find(anchor => anchor.id === anchorId);
    },


    getPhaseById: (state) => phaseId => {
      return state.project.phases.find(phase => phase.id === phaseId);
    },

    getCollaboratorSignTask: (state,getters) => collaboratorId => {
      return getters.signerTasks.find(task => task.collaboratorId === collaboratorId);
    },

     /**
     * Gets phase steps.
     *
     * To avoid data duplication step-data is only contained in the anchors.
     */

     getPhaseSteps:(state) =>phaseId =>{
      let phaseSteps = [];
       state.project.anchors.forEach(anchor => {
         anchor.steps.forEach(step =>{
           if(step.phaseRef === phaseId)
             phaseSteps.push(step);
         });
       });
       return phaseSteps;
    },

     /**
     * Get step tasks.
     */
     getStepTasks: (state) => stepId => {

      if(state.project.tasks)
        return state.project.tasks.filter(task => task.stepId ===stepId);

      return [];
    },

     /**
     * Search for any possible Previous and Next Step
     *
     * Possible Previous and Next Steps are located
     * in different Phases.
     *
     * - Check for every Phase
     * - Check for every Steps in the Anchor that are included in each Phase
     * - If the Step is located prior the current Phase, then it is Previous Step
     * - If the Step is located after the current Phase, then it is Next Step
     */
     getPrevNextStep: (state) => (currentPhaseId) => {
      let phases = state.project.phases;
      let anchor = state.project.anchors[0];

      let phase;

      let prevStepId;
      let nextStepId;
      let isAfterCurrentPhase = false;
      let isFinish = false;

      for (let i = 0; i < phases.length && !isFinish; i++) {
        phase = phases[i];
        if (phase.id == currentPhaseId) {
          isAfterCurrentPhase = true;
        }

        for (let j = 0; j < anchor.steps.length && !isFinish && phase.id != currentPhaseId; j++) {
          if (anchor.steps[j].phaseRef == phase.id) {
            if (!isAfterCurrentPhase) {
              prevStepId = anchor.steps[j].id;
            } else {
              nextStepId = anchor.steps[j].id;
              isFinish = true;
            }
          }
        }
      }

      return {
        previous: prevStepId,
        next: nextStepId
      }

    },

    /**
     * Get project document by identity.
     *
     */
    getDocumentByID: (state) => documentId => {
      if(state.project.projectDocuments)
        return state.project.projectDocuments.find(document => document.id === documentId);

      return null;
    },

    /**
     * Get current logged-in user's Team information (if any)
     *
     */
     getUserTeam : (state, getters) => {
      let collaboratorId = getters.getCollaboratorId;
      let teams = state.project.teams;

      if(teams)
        return teams.find(team => team.collaborators.some(collaborator => collaborator.id == collaboratorId));
      else
        return null;
    },
    /**
    * Gets the current logged in user's Collaborator identity (if any).
    *
    */
    getCollaboratorId: (state,getters,rootState,rootGetters) => {
      let userId = getters.getUserId;

      if(userId == 'guest') {
        let collaboratorId = rootGetters.getGuestCollaboratorId
        if(!collaboratorId)
          console.error("Guest collaborator id missing");
        return collaboratorId;
      } else {
        let collaborator = getters.getCollaboratorByUserId(userId);

        return collaborator != null ? collaborator.id : null;
      }
    },

    /**
     *  Get collaborator avatar image URL.
     *
     * - If the collaborator is the current logged-in user who has avatar, then the up-to-date avatar URL
     * is fetched from users.js store state, which is updated every time the user updates their
     * avatar image,
     * - Else the avatar image URL is returned if the user associated to that collaborator
     *  has an avatar image.
     *
     */
    getCollaboratorAvatarURL:(state,getters) => collaboratorId => {

      if(getters.getCollaboratorId == collaboratorId) { // Logged-in user
        return getters.hasUserAvatar?getters.getAvatarURL:null;
      }
      else {
        let collaborator = getters.getCollaboratorById(collaboratorId);
        if(collaborator.hasAvatar)
          return window.FlowSignOnlyWebsiteGlobalConfig.basePaths.flowController +
          "/v1/users/" + collaborator.userId + "/avatar"
      }
    },

    /**
     * Gets the collaborator associated to the logged in user in the current project.
     */
    getCollaborator: (state,getters) => {
      return getters.getCollaboratorById(getters.getCollaboratorId);
    },

    /**
     * Get list of project Documents from Store.
     */
    getProjectDocuments: (state) => {
      return state.project.projectDocuments;
    },

    isOverlaySignaturesEnabled: (state )=> {
      return state.project.overlaySignaturesEnabled;
    },

    isSmsAuthenticationEnabled: (state )=> {
      return state.project.authenticationMethod == "SMS";
    },


    smsAuthenticatedTasksWithoutPhonenumber: (state) => {
      return state.project.smsAuthenticatedTasksWithoutPhonenumber;
    },

    /**
     * Returns a project owner of the current project.
     */
    getProjectOwner: (state) => {
      let collaborators = state.project.collaborators != null ?
                          state.project.collaborators :
                          [];

      return collaborators
        .find(collaborator => collaborator.projectRole === "PROJECT_OWNER");
    },
  },

  mutations: {

    setScheduledProjectReloadCanceled(state,newValue) {
      state.scheduledProjectReloadCanceled = newValue;
    },

    setScheduledProjectReload(state,newValue) {
      state.scheduledProjectReload = newValue;
    },

    cancelScheduledProjectReload(state) {
      state.scheduledProjectReloadCanceled = true;
    },

    /**
     * Reset the Project data in Store.
     *
     * This is used when closing Project page and is needed so that there won't
     * be previous Project data visible when opening another Project.
     */
    resetProjectStore(state) {

      // If there is a scheduled project reload: cancel it now because we do not
      // wish that it fires after we have reset the project data. After reset
      // there is not even a project identity which could be used for fetching
      // the data so the project data reload will cause an error.

      if(state.scheduledProjectReload)
        state.scheduledProjectReload.cancel();

      // Make sure this is 'false' because if it is left 'true' the next
      // 'getProject' action call will not update the state.

      state.scheduledProjectReloadCanceled = false;
      state.scheduledProjectReload = null;

      this.commit("resetProject");
    },

    /**
     * Empty the Project data in Store
     *
     * This is used when closing Project page.
     *
     */
     resetProject(state) {
      delete state["project"];
      state.project = {};

      delete state["annotations"];
      state.annotations = [];
    },

    setAnnotations(state,annotations) {
      state.annotations = annotations;
    },

    /**
     * Sets Project data to Store
     */
    setProject(state, project) {
      state.project = project;
    },

     /**
     * Updates a project setting.
     *
     * @param {*} state
     * @param {*} setting object with "key" and "value" fields
     */
     updateProjectSetting(state,setting) {
      state.project[setting.key] = setting.value;
    },

    addTask(state, params) {
      let task = {
        "id":params.id,
        "taskStatus" : params.taskStatus,
        "description" : params.description,
        "stepId" : params.stepId,
        "taskRole" : params.taskRole,
        "collaboratorId" : params.collaboratorId,
      };

      state.project.tasks.push(task);
    },

    deleteTask(state, params) {
      state.project.tasks =
          state.project.tasks.filter(task => task.id !== params.taskId);
    },

     /**
     * Adds the step to the store state.
     * The neighbouring steps hold a reference to
     * the step in field "next"/"previous" depending on the order.
     *
     * Notice that this might not fully update the state. This is currently used
     * to have a quick response from the UI to step addition as well as
     * provide the step reference to the store state to enable assingment
     * addition to the step.
     *
     * @param {*} state
     * @param {*} params
     */

     addStep(state,params) {

      let anchor = this.getters.getAnchorById(params.stepAnchor);
      let previousStep = this.getters.getStepById(params.previous);
      let nextStep = this.getters.getStepById(params.next);

      // Update the neighbouring step next/pref references
      if(previousStep)
        previousStep['next'] = params.id;
      if(nextStep)
        nextStep['previous'] = params.id;

      // Update the anchor:

      // If previous step is not defined, it must be the first step;
      let anchorStepIdx = 0 ;

      if(!anchor.steps) // Initialize anchor.steps array. Should not happen.
        anchor["steps"] = [];

      for(let idx = 0; idx < anchor.steps.length; idx++){
        if(anchor.steps[idx].id == params.previous){
          anchorStepIdx = idx+1;
          break;
        }
      }

      let anchorSteps = [...anchor.steps];

      anchorSteps.splice(anchorStepIdx,0, params);
      anchor["steps"] = anchorSteps;
    },

    /**
     * Deletes a step from the store state.
     *
     * Removes the assingments related to that step from the store state.
     * Removes the references to the previous and next steps.
     * Updates the reference in the owning anchor and phase.
     *
     * params:
     *  - stepId: identity of the removed step
     *  - deleteTasks: whether to delete tasks also
     *
     * @param {*} state
     * @params {*} params
     *
     */

    deleteStep(state, params) {

      let deleteTasks = params.deleteTasks;
      let stepId = params.stepId;
      let store = this;
      let step = this.getters.getStepById(stepId);

      if(deleteTasks){
        this.getters.getStepTasks(step.id).forEach(function (task) {
          let params = {
            "stepId": stepId,
            "taskId": task.id,
          };
          store.commit("deleteTask",params);
        });
      }

      let anchor = this.getters.getAnchorById(step.stepAnchor);
      let previousStep = this.getters.getStepById(step.previous);
      let nextStep = this.getters.getStepById(step.next);

       if (previousStep)
          previousStep['next'] = step.next;
       if (nextStep)
          nextStep['previous'] = step.previous;

       // If previous step is not defined, it must be the first step;
       let anchorStepIdx = anchor.steps.findIndex(step => step.id===stepId);
       let anchorSteps = [...anchor.steps]

       anchorSteps.splice(anchorStepIdx, 1);
       anchor["steps"] = anchorSteps;
     },

     /**
     * Moves step.
     *
     * Moves the step by remove - add actions.
     *
     * params: {
     *   id : step identity
     *   newAnchorRef: new anchor identity
     *   newPhaseRef: new phase identity
     *   newPrevRef: new previus step identity
     *   newNextRef: nex next step identity
     * }
     *
     * @param {*} state
     * @param {*} params
     */

    moveStep(state,params) {
      let stepId = params.id;
      let step = this.getters.getStepById(stepId);
      let deleteTasks = false;

      this.commit("deleteStep",{stepId,deleteTasks});

      // Update the step data.
      step.stepAnchor = params.newAnchorRef;
      step.phaseRef = params.newPhaseRef;
      step.previous = params.newPrevRef;
      step.next = params.newNextRef;

      this.commit("addStep",step);
    },


     /**
     * Adds a new collaborator to state.
     *
     * Notice that this does not create a new collaborator if a matching
     * collaborator with status "INACTIVE" already exists. Instead we just
     * change that collaborator's project role.
     *
     * Expected parameters:
     *
     * params = {
     *   "projectId": string,
     *   "userId": string,
     *   "fullName" : string,
     *   "projectRole": string,
     *   "email": string
     * }
     * @param {*} state state
     * @param {*} params parameters object
     */

     addCollaborator(state,params) {
      // First check if there is already a "INACTIVE" collaborator with the matching
      // identity (user, or email) and update the role of that collaborator instead
      // of adding a new one.
      let collaborator = null;

      if(params.userId)
        collaborator = this.getters.getCollaboratorByUserId(params.userId);
      else if(params.email)
        collaborator = this.getters.getCollaboratorByUserEmail(params.email);

      if(collaborator != null) {
        if(collaborator.projectRole == "INACTIVE")
          this.commit("updateCollaborator", params);
        // else: do nothing, the collaborator already exists in state.
      }
      else {

        // Note: Some of the information is null because we simply do not know
        //       that. The data will be refreshed when the collaborator is
        //       added finally to DB. The most notable field that might be missing
        //       is the (collaborator) "id" (link between user and project).

        let stateParams = {
          "tasks": [],
          "id": params.id,
          "fullName": params.fullName,
          "userId": params.userId,
          "projectRole": params.projectRole,
          "defaultTaskRole": params.defaultTaskRole,
          "email": params.email,
          "teamId": params.teamId,
        }

        state.project.collaborators = [...state.project.collaborators, stateParams];
      }
    },

    /**
    * Updates a single collaborator. Tries to find a Collaborator entity that
    * has the same user than the parameters.
    *
    * Expected parameters:
    *
    * params = {
    *  "id": "string",
    *  "stepId": "string",
    *  "fullName": "string",
    *  "userId": "string",
    *  "projectRole": "string",
    *  "email": "string"
    *	"teamId": "string",
    * }
    *
    * @param {*} state
    * @param {*} params
    */

    updateCollaborator(state, params) {

      let collaborators = this.getters.getProjectCollaborators;

      // DOS-814:
      // Firstly, search new collaborator by collaborator ID.
      // If none, search new collaborator by userId if he/she is in the same organization
      // or by email if he/she is in different org or not registered
      let collaborator;

      if(params.id != null)
        collaborator = collaborators.find(c => c.id === params.id);
      if(!collaborator && params.userId != null)
        collaborator = collaborators.find(c => c.userId === params.userId);
      if(!collaborator && params.email != "Email unavailable")
        collaborator = collaborators.find(c => c.email === params.email);


      if(collaborator != null) {
        if(params.id != null)
          collaborator["id"] = params.id;
        if(params.collaboratorType != null)
          collaborator["collaboratorType"] = params.collaboratorType;
        if(params.fullName != null)
          collaborator["fullName"] = params.fullName;
        if(params.projectRole != null)
          collaborator["projectRole"] = params.projectRole;
        if(params.defaultTaskRole != null)
          collaborator["defaultTaskRole"] = params.defaultTaskRole;
        if(params.email != null)
          collaborator["email"] = params.email;
        if(params.id != null)
          collaborator["id"] = params.id;
        if(params.locale != null)
          collaborator["locale"] = params.locale;
        if(params.teamId != null)
          collaborator["teamId"] = params.teamId;
        if(params.signerDetails != null)
          collaborator["signerDetails"] = params.signerDetails;
        if(params.hasAvatar !=null)
          collaborator["hasAvatar"] = params.hasAvatar;
      }
    },

    /**
     * Updates collaborator's locale in store state.
     *
     * Expected parameters:
     *
     * params = {
     *   "collaboratorId": String,
     *   "locale": String
     * }
     */
    updateCollaboratorLocale(state, params) {
      const collaborator = this.getters.getCollaboratorById(params.collaboratorId);
      if(collaborator)
        collaborator.locale = params.locale;
    },

    updateCollaboratorSignerDetails(state,params) {
      let collaborator = this.getters.getCollaboratorById(params.collaboratorId);

      if(params.hasOwnProperty('title'))
        collaborator.signerDetails.title=params.title;
      if(params.hasOwnProperty('fullName'))
        collaborator.signerDetails.fullName=params.fullName;
      if(params.hasOwnProperty('organization'))
        collaborator.signerDetails.organization=params.organization;

    },

     /**
     * Adds a new Document to state.
     *
     * Expected parameters:
     *
     * params = {
     *   "id": string,
     *   "fileName": string,
     *   "fileLocation" : string,
     *   "projectId" : string,
     *   "uploaderId": string,
     *   "creationDate": string,
     *   "modificationDate": string,
     * }
     */
    addDocument(state, params) {
      let index =
        state.project.projectDocuments ? state.project.projectDocuments.length : 0;

      state.project.projectDocuments[index] = params;
    },

    /**
     * Deletes a Project Document from the state.
     *
     * @param {String} documentId document identity
     */
    deleteDocument(state, documentId) {
      state.project.projectDocuments =
          state.project.projectDocuments.filter(document => document.id != documentId);
    },

    /**
     * Attaches a new document to anchor.
     *
     * Expected parameters:
     * document: object
     *
     * @param {*} state state
     * @param {*} params parameters object
     */

     attachDocument(state, document) {
      let anchor = this.getters.anchor
      anchor["anchorDocument"] = document;
      state.project.anchors.splice(0,1,anchor);

    },

    updateDocumentName(state, newName) {
      let document = this.getters.document;
      document["fileName"]= newName;
    },

    /**
     * Updates given task status.
     *
     * Expected parameters:
     * params = {
     *   taskId: String,
     *   taskStatus: String
     * }
     */

    updateTaskStatus(state, params) {
      let task = this.getters.getTaskById(params.taskId);
      task["taskStatus"] = params.taskStatus;
    },

     /**
     * Updates the SMS authentication phone number of a collaborator
     *
     * Expected parameters:
     *
     * params = {
     *   "id": "string",
     *   "smsAuthPhoneNumber": "string"
     * }
     *
     * @param {*} state
     * @param {*} params
     */

     updateCollaboratorSmsAuthPhoneNumber(state, params) {

      let collaborators = this.getters.getProjectCollaborators;
      let collaborator = collaborators.find(collaborator => collaborator.id === params.collaboratorId);
      if(collaborator) {
        collaborator["smsAuthPhoneNumber"] = params.smsAuthPhoneNumber;
      }
    },
  },

  actions: {

    /**
     * Schedules a current project reload. Reloads the current project data
     * from backend API and refreshes the data in Store with it.
     *
     * Do note that this should be used only in cases where the UI has already
     * been updated and we just wish to fetch the authoritative version from
     * backend. Also note that this function is "debounced" so first call
     * within the timeframe schedules the update and subsequent calls to this
     * function will simply be ignored until the update is finally actually
     * fired.
     *
     * Because of the debouncing this action is not usable if the state needs to
     * be updated ASAP. Main idea with this action is to perform the project
     * reload after all the UI changes have been done and if new UI changes
     * are done the project reload is delayed further until no new UI changes
     * are encountered. This will fix issues where the UI is updated multiple
     * times and then authoritative update from backend overwrites the UI
     * changes.
     *
     * @param {*} context
     */

    scheduleProjectReload: function(context) {
      if(!context.getters.scheduledProjectReload) {
        context.commit("setScheduledProjectReload", debounce(function(context) {
          // Set the "isCanceled" flag false. If some event cancels the project
          // reload between this point and when we actually reload the
          // project data, discard the project data.

          context.commit("setScheduledProjectReloadCanceled",false);

          // Note: "debounce" comes from Lodash.
          // The reloads to current project are debounced. Reason for this is that
          // if multiple requests are encountered we do not need to fulfil them
          // all.
          // Note: If user has logged out then do not try to load project data, it will result a fail.
          //       This may happen if a project reload is scheduled and user logs out before the
          //       reload is fired.
          // Note: Do not try to load project if the project identity is not defined.
          let projectId =  context.getters.projectId;

          if(context.getters.isAuthenticated && projectId!=null )
            context.dispatch("getProject", projectId);

        },4000));
      }

      context.getters.scheduledProjectReload(context); // Execute.
    },

    /**
     * Get Project data from API and then put the data in Store.
     *
     * The action returns Promise so that the component that use it can decide
     * what to do if the API call fails.
     *
     * @param {string} projectId
     */
    async getProject(context, projectId) {

      // If no projectId is given, we consider this a "reload" request.

      if(!projectId)
        projectId = context.getters.projectId;
      if(!projectId)
        throw "Can not call query getProject, projectId is not defined.";

      try {

        // Pass the project modification date in "Modification-Date" custom header, DOS-1746.
        let hasLastModified = context.getters.project && context.getters.project.modificationDate
        let config = {};

        if(hasLastModified){
           config = {
            headers: {
              'Modification-Date': context.getters.project.modificationDate,
            }
          }
        }

        let url = '/v1/projects/' + projectId;

        const result = await apiCall.get(url,config);

        const project = result.data;

        // TODO: Enforce sign-only project here.
        // If the "projectReloadCanceled" flag is on, ignore this project
        // update.

        if(context.getters.scheduledProjectReloadCanceled !== true) {
          const project = result.data;
          logger.debug("Query getProject succeeded with result:",
                              project);
          context.commit("setProject", project);
        }
        context.commit("setScheduledProjectReloadCanceled",false);

        return result;
      }
      catch(error) {

        if(error.response.status == "304"){
          logger.debug("Query getProject didn't return data since the project data is up to date.")
        }
        else{
          logger.error("Query getProject didn't succeed with message:", error);
          throw error;
        }
      }
    },

    /**
     * Delete project from database.
     */
    async deleteProject(context, projectId) {

      try {
        const result = await apiCall.delete('/v1/projects/'+ projectId)
        logger.debug("DELETE project succeeded with result:", result);
        return result;
      }
      catch(error) {
        logger.error("DELETE project didn't succeed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

    async updateDocumentName (context, updateRequest) {
      try {
        context.commit("updateDocumentName", updateRequest.fileName);
        const result = await apiCall.patch('/v1/documents/project/filename/change', updateRequest)
        logger.debug("Query Update filename succeeded with result:", result);
      }
      catch(error) {
          logger.error("Query update file name didn't succeed with message:", error);
          context.dispatch("getProject");
          throw error;
      }
    },

    /**
     * Set project to live status.
     *
     * Note: this leaves the project data in wrong state.
     * We expect the caller to reload the project data after in case needed.
     *
     */
    async setProjectLive(context, projectId) {

      try {
        const result = await apiCall.put('/v1/projects/' + projectId + '/live')
        logger.debug("Project has been set to Live status");
        return result;
      }
      catch(error) {
        logger.error("Setting project to Live status failed.");
        logger.error(error.response);
        // reload project data from database
        context.dispatch("getProject");
        throw error;
      }
    },


    /**
     * Set Project to go to paused state.
     */
    async setProjectPaused(context, projectId) {

      try {
        const result = await apiCall.put('/v1/projects/' + projectId + '/pause')
        logger.debug("Project has been Paused.");
        return result;
      }
      catch(error) {
        logger.error("Pausing project failed.");
        logger.error(error.response);

        // Reload project data from database.
        context.dispatch("getProject");
        throw error;
      }
    },


    /**
     * Updates a Project Setting.
     *
     * How to use:
     * this.$store.dispatch("updateProjectSetting", {'key': settingName,'value': settingValue})
     * or
     * this.$store.dispatch("updateProjectSetting", {'key': settingName,'value': settingValue}, projectId)
     * projectId is needed when we are in Projects page
     */
    async updateProjectSetting(context, {key,value,projectId}) {

      let params = {
        id: projectId ? projectId : context.state.project.id
      };

      params[key] = value;

      try {
        const result = await apiCall.put('/v1/projects/update/settings', params);
        logger.debug("PUT updateProjectSettings succeeded with result:", result);
        context.commit("updateProjectSetting", {'key': key, 'value': value});
        return result;
      }
      catch(error) {
        logger.error("PUT updateProjectSettings didn't succeed with message:", error);

        // Note: Get Project data again from back-end.
        context.dispatch("getProject");
        throw error;
      }
    },

    /**
     * Calls back-end to delete a task.
     */
    async deleteTask(context, taskId) {


      let callParams = {
        "taskId": taskId
      }

      // Commit the change right away so the UI will be updated.
      context.commit("deleteTask", callParams);

      try {
        const result = await apiCall.delete('/v1/tasks/delete', {
          data: callParams
        });

        logger.debug("deleteTask succeeded with result:", result);
        return result;
      }
      catch(error) {
        logger.error("deleteTask failed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        throw error;
      }
    },

     /**
     * Add a Collaborator into Project.
     *
     * Notice that both the back end implementation and the store state mutators
     * behave similarly in case when a collaborator is added, but a matching
     * INACTIVE one already exists. In these case the inactive collaborator's
     * project role is changed from INACTIVE to something else.
     *
     * Expected parameters are as follows:
     *
     * let params = {
     *   userId: "String", (optional either userId or email must given)
     *   fullName: "String",
     *   email: "String", (optional either userId or email must given)
     *   projectId: "String" (optional, if null will use current project id),
     *   projectRole: "String" (optional, if null will use "COLLABORATOR")
     * };
     *
     * @param {*} context context
     * @param {*} params parameters object
     *
     * @returns {Promise} promise
     */

     async addCollaborator(context, params) {

      let projectId = params.projectId != null ?
                      params.projectId :
                      context.state.project.id;

      let projectRole = params.projectRole != null ?
                        params.projectRole :
                        "COLLABORATOR";

      let stateParams = {
        "id": null, // Note: We do not yet know the Collaborator's identity.
        "projectId": projectId,
        "projectRole": projectRole,
        "userId": params.userId,
        "fullName": params.fullName,
        "email": params.email
      }

      // Commit mutation right away to state so end-user will see the visual
      // change right away (i.e. UI will feel responsive).

      this.commit("addCollaborator", stateParams);

      if (params.userId) {

        let apiParams = {
          "projectId": projectId,
          "projectRole": projectRole,
          "userId": params.userId
        }

        try {
          const result = await apiCall.put('/v1/users/project/link', apiParams)
          logger.debug("addCollaborator succeeded with result:",
                              result);

          // Update the state with the latest collaborator information. This
          // will set e.g. the "collaboratorId".

          context.commit("updateCollaborator", result.data);

          // TODO: Consider enabling the following to fetch the authoritative
          //       state:
          // "context.dispatch("scheduleProjectReload");"

          return result;
        }
        catch(error) {
          logger.error("addCollaborator didn't succeed with message:",
                              error);

          context.dispatch("getProject"); // Forced update right away.
          throw error;
        }
      }
      else { // User added by email, invited.

        let apiParams = {
          "projectId": projectId,
          "email": params.email
        }

        try {
          const result = await apiCall.put('/v1/invitation/external', apiParams)
          logger.debug("addCollaborator succeeded with result:",
                              result);

          // Update the state with the latest collaborator information. This
          // will set e.g. the "collaboratorId".

          context.commit("updateCollaborator", result.data);

          // TODO: Consider enabling the following to fetch the authoritative
          //       state:
          // "context.dispatch("scheduleProjectReload");"

          return result;
        }
        catch(error) {
          logger.error("addCollaborator didn't succeed with message:",
                              error);

          context.dispatch("getProject"); // Forced update right away.
          throw error;
        }

      }
    },

    /**
     * Adds a new task.
     *
     * Note: Signer tasks are all set "signingOnBehalfOfOrganizaton" to TRUE.
     *
     * Expected parameters:
     *
     * params = {
     *   collaboratorId: "String",
     *   description: "String",
     *   stepId: "String",
     *   taskRole: "String"
     * }
     *
     * @param {*} context
     * @param {*} params data of the new task
     */

    async addTask(context, {params,}) {
      let result;

      try {
        result = await apiCall.post('/v1/tasks/new', params)
        logger.debug("addTask succeeded with result:", result);
        context.commit("addTask", result.data);
      }
      catch(error) {
        logger.error("addTask didn't succeed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        context.dispatch("getProject");

        throw error;
      }

      // NOTE: Update "Task.signingOnBehalfOfOrganizaton" to TRUE for sign tasks.
      //       "Task.signingOnBehalfOfOrganizaton" is not included in the cached state
      //        of the task.

      if(params.taskRole == "SIGNER") {
        try{
          let updateParams = {taskId:  result.data.id,
                              signingBehalfOrganizationEnabled: true};

          const updateResult =
            await apiCall.patch('/v1/tasks/signing-behalf-organization', updateParams);
        }
        catch(error) {
          logger.error("Enabling signing on behalf of an organization failed", error);

          // Delete the task. User can try again.
          await this.dispatch("deleteTask", result.data.id);

          throw error;
        }

      }

      return result;
    },

     /**
     * Add a Step data to database through API.
     *
     */
     async addStep(context, params) {

      try {
        const result = await apiCall.post('/v1/steps/add', params)
        logger.debug("addStepToProject succeeded with result:", result);
        context.commit("addStep",result.data);
        return result;
      }
      catch(error) {
        context.dispatch("getProject");
        logger.error("addStepToProject didn't succeed with error:",error);
        logger.error(error.response);
        throw error;
      }
    },

    /**
     * Delete Step
     *
     * Expected parameter: stepId
     */
    async deleteStep(context, stepId) {

      let deleteTasks = true;

      this.commit("deleteStep",{stepId, deleteTasks});

      try {
        const result = await apiCall.delete('/v1/steps/removal/' + stepId)
        logger.debug("deleteStep succeeded with result:", result);
        return result;
      }
      catch(error) {
        context.dispatch("getProject");
        logger.error("deleteStep didn't succeed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

    async reorderStep(context, newOrder) {

      context.commit("moveStep",newOrder);

      try {
        const result = await apiCall.patch('/v1/steps/reorder', newOrder)
        logger.debug("updateStepOrder succeeded with result:", result);
        return result;
      }
      catch(error) {
        context.dispatch("getProject");
        logger.error("updateStepOrder didn't succeed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        throw error;
      }
    },

    /**
     * Updates collaborator signer details.
     *
     * Expected "signerDetails" fields:
     *  String taskId;
     *  String email;
     *  String fullName;
     *  String initials;
     *  String organization;
     *  String title;
     *
     *
     * @param {JSON} signerDetails
     */

    async updateSignerDetails(context,signerDetails) {
      try {
      const result = await apiCall.patch('/v1/signer-details',signerDetails);
      }
      catch(error) {
        context.dispatch("getProject");
        logger.error("Signer details updadate failed", error);
        throw error;
      }
    },

    /**
     * Updates the signer's locale. Optimistically updates the UI and reverts
     * the change if the API call fails.
     *
     * Expected parameters:
     *
     * params = {
     *   "collaboratorId": String,
     *   "locale": String
     * }
     */
    async updateSignerLocale(context, params) {
      const collaborator = context.getters.getCollaboratorById(params.collaboratorId);
      if(!collaborator)
        return;

      const previousLocale = collaborator.locale;

      context.commit("updateCollaboratorLocale", params);

      try {
        await apiCall.put('/v1/users/collaborator/locale', {
          collaboratorId: params.collaboratorId,
          locale: params.locale
        });
      }
      catch(error) {
        logger.error("Signer locale update failed", error);

        context.commit("updateCollaboratorLocale", {
          collaboratorId: params.collaboratorId,
          locale: previousLocale
        });

        throw error;
      }
    },


    /**
     * Creates a new project and returns the project identity if creation succeeds.
     *
     * FIXME: Would better to do all this with just one API call.
     *
     * @returns new, not yet attached to anchor, project document.
     *
     * @param file selected document
     */
    async createProject(context, file) {

      let requestParams = {
        signOnlyProject: "true",
        name: file.name // TODO: Is this right?
      };

      let newProjectId;
      let newDocumentId;
      let newAnchorId;

      // 1. Create a new project.
      try {
        const projectResponse = await apiCall.post('/v1/projects/new', requestParams);
        context.commit("setProject",projectResponse.data);

        newProjectId = projectResponse.data.id;
        newAnchorId = projectResponse.data.anchors[0].id
      }
      catch(error){
        logger.error("Project creation failed",error);
        throw error;
      }

      let params = {'projectId': newProjectId,
                    'file': file,
                    fileLocation: "PUBLIC"
      };

      // 2. Upload the selected document to the new project documents. Notice that the
      //    document is not yet attached to the anchor.
      let uploadResponse;

      try{
        let result = await context.dispatch("uploadDocument",params);
        uploadResponse = result.data;
        newDocumentId = uploadResponse.currentRevision.documentId;
      }
      catch(error){
        logger.error("Document upload failed",error)
        context.dispatch("deleteProject", newProjectId); // Delete the created project, SOFLOW-93.
        throw error;
      }
      return uploadResponse;
    },

    /**
    * Deletes document.
    */
    async deleteDocument(context, documentId) {
      let url = '/v1/documents/project/' + documentId;

      try {
        const result = await apiCall.delete(url);
        logger.debug("Document deletion succeeded with result:",result);
        context.commit("deleteDocument",documentId);
        return result;
      }
      catch(error) {
        logger.error("Document deletion failed:",error);
        throw error;
      }
    },

    /**
     * Detach project document from anchor.
     *
     * NOTE: This does not update store state!
     */
    async detachDocument(context) {
      let url = '/v1/anchors/' + this.getters.anchorId + '/detach';

      try {
        const result = await apiCall.put(url);
        logger.debug("Document detach succeeded with result:", result);
        return result;
      }
      catch(error) {
        logger.error("Document detach failed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        // Reload project data right away because the UI data might be in
        // out of sync.

        context.dispatch("getProject");

        throw error;
      }
    },

    /**
     * Uploads a file to server.
     *
     * Note: This does not update the store state.
     *
     * Do note that if the "projectId" and "fileLocation" are null/undefined then the upload
     * document will be considered a User's private Document. Otherwise it will be considered
     * a Project Document.
     *
     * Expected parameters:
     *
     * params = {
     *   file: (object),
     *   projectId: (String),
     *   fileLocation: (String),
     * }
     *
     * @params context
     * @params params
     *
     * @returns {Promise} promise
     */

    async uploadDocument(context, params) {

      let apiFormData = new FormData();

      let file = params.file
      let url;

      apiFormData.append("file",file,file.fileName);
      apiFormData.append("projectId",params.projectId);
      apiFormData.append("fileLocation",params.fileLocation);
      url = '/v1/documents/project/uploadFile';

      try{
        let response = await apiCall.post(url, apiFormData)
        return response;
      }
      catch(error) {  // A single file upload failed
        logger.debug("uploadDocument: Upload failed:",error);
        throw error;
      }
    },

    /**
       * Attach Document to Workflow Anchor.
       *
       *  Note: This does not update the store state.
       *
       * required params:
       * {
       *  "anchorId": "string",
       *  "documentId": "string"
       * }
       *
       */
    async attachDocument(context, params) {
      let url = '/v1/anchors/' + params.anchorId + '/attach';

      try {
        let apiParams = {
          documentId: params.documentId
        };

        const result = await apiCall.put(url, apiParams);
        return result;
      }
      catch(error){
        logger.error("attachDocument failed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        // Reload project data right away because the UI data might be in
        // out of sync.

        context.dispatch("getProject");
        throw error;
      }
    },

    async renameDocument (context,params) {
      let url = "/v1/documents/project/filename/change";
      try{
        await apiCall.patch(url, params);
        context.commit("updateDocumentName",params.fileName);
      }
      catch(error) {

      }
    },

    async uploadVisualSignature(context,params) {
      let apiFormData = new FormData();
      apiFormData.append("signature",params.signatureBlob);
      apiFormData.append("signatureTemplateId",params.signatureTemplateId);

      let collaboratorId = params.collaboratorId;
      let collaborator = context.getters.getCollaboratorById(collaboratorId);

      let url = '/v1/signer-details/' + collaboratorId + '/signature';

      let signerDetails = collaborator.signerDetails;
      let signerDetailsClone = {...signerDetails};

      try {
        // Update state.
        signerDetailsClone.definesVisualSignature = true;
        signerDetailsClone.signatureTemplateId = params.signatureTemplateId; // DOS-3052.

        params = {
          "id": collaboratorId,
          "signerDetails": signerDetailsClone
        };

        context.commit('updateCollaborator',params);

        // Call back-end.

        const result = await apiCall.post(url, apiFormData);

        logger.debug("uploadSignature succeeded with result:", result);
        return result;
      }
      catch(error) {
        logger.error("uploadSignature failed with message:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);

        // Reset state on back-end call failure.

        params = {
          "id": collaboratorId,
          "signerDetails": signerDetails
        };

        context.commit('updateCollaborator',params);

        throw error;
      }
    },

    /**
     * Marks a task as "Rejected".
     *
     * Expected parameters:
     *
     * params = {
     *   "taskId": String,
     *   "message": String
     * }
     *
     * @returns {Promise} promise
     */
    async setTaskAsRejected(context, params) {

      let url = '/v1/tasks/reject/task';

      try {
        const result = await apiCall.put(url, params)
        logger.debug("setTaskAsRejected succeeded:", result);
        context.dispatch("getProject");
        return result;
      }
      catch(error) {
        logger.error("setTaskAsRejected failed:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

    async pauseStep(context, stepId) {
      let url = '/v1/steps/pause/' + stepId;

      try {
        const result = await apiCall.patch(url);
        logger.debug("pauseStep succeeded:", result);
      }
      catch(error) {
        logger.error("pauseStep failed:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

      /**
     * Mark one task as "Complete"
     *
     * Expected parameter:
     *
     * params = {
     *   taskId: String,
     *   completeStep: Boolean,
     *   message: String
     * }
     */
      async completeTask(context, params) {

        let url = 'v1/tasks/complete';

        try {
          const result = await apiCall.put(url, params)
          logger.debug("setTaskAsComplete succeeded:", result);

          let mutationParams = {
            taskId: params.taskId,
            taskStatus: tasks.STATUS.COMPLETED
          };
          context.commit("updateTaskStatus",mutationParams);
          return result;
        }
        catch(error) {
          logger.error("Task completion failed:", error);
          logger.error(error.response.data);
          logger.error(error.response.status);
          logger.error(error.response.headers);
          throw error;
        }
      },

    async revertStep(context, params) {
      console.log(params)
      let url = '/v1/steps/revert';
      try {
        const result = apiCall.patch(url, params);
        logger.debug("revertStep succeeded:", result);
        return result;
      }
      catch(error) {
        logger.error("revertStep failed:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

    async loadAnnotations(context,documentId) {
      try{
        let response = await apiCall.get("/v1/annotations/on-document/" + documentId);
        // Filter out the deleted.
        let annotationsResponse = response.data.filter(annotation => !annotation.latestRevision.deleted);
        context.commit("setAnnotations",annotationsResponse);
        return annotationsResponse;
      }
      catch(error){
        logger.error("Failed to load document annoations:", error);
      }
    },

    async deleteAllAnnotations(context){
      // Load the latest state of document annotations before deleting.
      await context.dispatch("loadAnnotations", context.getters.document.id);

      let annotations = context.getters.getAnnotations;

      await Promise.all(annotations.map(annotation => {
        return apiCall.delete("/v1/annotations/" + annotation.id);
      }));
      context.commit("setAnnotations",[]);
    },


    /**
     * Removes a Collaborator from the Project.
     */
    async removeCollaborator(context, collaboratorId) {
      try {
        const result = await apiCall.delete('/v1/users/collaborator/' + collaboratorId)
        logger.debug("Query removeCollaborator succeeded:",result);
        return result;
      }
      catch(error) {
        logger.error("Query removeCollaborator failed:",error);

        // Force a state refresh, our current state might be in mismatch.
        context.dispatch("getProject");

        throw error;
      }
    },

    async completeStep(_context, stepId) {
      try {
        const result = await apiCall.patch(`/v1/steps/${stepId}/complete`);
        logger.debug("Step completion succeeded with result:", result);

        this.commit("setProject", result.data);
      }
      catch(error) {
        logger.error("Step completion failed with error:", error);
        logger.error(error.response);
        throw error;
      }
    },

    /**
     * Pause/unpause parallel signer tasks.
     */
    async toggleParallelTasks(context,params) {

      try {
        const result = await apiCall.put('/v1/tasks/toggle-parallel-task-statuses',params)
        // Reload project data.
        context.dispatch("getProject");
        return result;
      }
      catch(error) {
        logger.error("Paralllel task status toggling failed.",error);
        logger.error(error.response);
        // Reload project data.
        context.dispatch("getProject");
        throw error;
      }
    },

    /**
     * Uploads a new document to the project, and if it is DOCX validates it.
     *
     * @param {*} context
     * @param {*} newDocument
     *
     * @returns response object containing details of the result of the operation
     */

    async uploadAndValidate(context, newDocument) {
      // Uploading a document with the same name as the current one fails. Therfore we upload the new
      // one under a temporary name in case there is a conflict.
      let oldDocumentName = context.getters.document != null? context.getters.document.fileName:""
      let newDocumentId;
      let newDocumentResponse;

      // Response object
      let response = {
        success: null, // Whether document was uploaded and passed validation.
        newDocument: null, // New document object
        nameConflict: false, // If there was a name conflict when uploading
        oldDocumentName: oldDocumentName, // Old document name, as it was renamed.
        oldDocumentId:context.getters.document.id, // Document identity with the conflicting name.
        validationResult: null, // Validation result, containing the validation details.
      }

      if(newDocument.name == oldDocumentName) {

        response.nameConflict = true;
        response.oldDocumentName = oldDocumentName;

        newDocument = new File([newDocument], 'temp-'+ newDocument.name,
          {
            type: newDocument.type,
            lastModified: newDocument.lastModified,
          });
      }

      try {
        let uploadParams = {
          file:newDocument,
          projectId: context.getters.projectId,
          fileLocation: "PUBLIC",
        }

        newDocumentResponse = await context.dispatch("uploadDocument",uploadParams);
        newDocumentId = newDocumentResponse.data.currentRevision.documentId;
        response.newDocument = newDocumentResponse.data;

        // Validate DOCX.
        if(commons.isDOCX(newDocumentResponse.data)) {
         let validationResult = await context.dispatch("getDocumentValidationResult", newDocumentId);
          let hasTrackedChanges = validationResult.contentChangesCount > 0 ||
                                  validationResult.formattingChangesCount > 0;
          let hasUnresolvedComments = validationResult.openCommentsCount >0;

          if(hasTrackedChanges || hasUnresolvedComments){ // Invalid docx
            response.success = false;
            response.validationResult = validationResult;
          }
          else { // Valid docx.
            response.success = true;
          }
        }
        else { // non-docx.
          response.success = true;
        }
        return response;
      }
      catch(error) {
        logger.debug("Document upload and validation failed", error);
        throw error;
      }
    },

    /**
     * Replaces project document with already uploaded document.
     *
     * Expected paramters:
     *
     * 'newDocument': new document response data
     * 'nameConflict': If the new document name was in conflict and was renamed
     * 'oldName': The old name, which will be restore.
     *  'oldDocumentId' : The old project document identity
     *
     * @see uploadAndValidate(context, newDocument)
     *
     * @param {*} params
     */

    async replaceCurrentDocument(context, params) {
       // Attach the new  document to the project anchor.

       let attachDocumentParams = {
        "documentId": params.newDocument.id,
        "anchorId": context.getters.anchorId,
      }
      let oldDocumentId = params.oldDocumentId;

      try {
        await context.dispatch("attachDocument",attachDocumentParams);

        // Update state only if there was no conflict in name. If there was, the state will be updated
        // only after restoring the old name.
        if(!params.nameConflict)
          context.commit("attachDocument", params.newDocument);
      }
      catch(error) {
        logger.error(error);
        // If for some reason the document does not attach to the anchor, we delete it to prevent
        // documents from cumulating in the project.
        await context.dispatch("deleteDocument", params.newDocument.id);

        context.dispatch("getProject");
        throw error;
      }

      // New was uploaded and attached to the project anchor. Delete the old document.
      try {
        await context.dispatch("deleteDocument", oldDocumentId);
      }
      catch(error){
        logger.error(error);
        // If the old deletion fails the new document rename will most likely fail as well
        // but try the anyway. This does not prevent the project using the new document.
      }
      // Restore the original name in case of conflict.
      if(params.nameConflict) {
        try {
          await context.dispatch("renameDocument", {'documentId': params.newDocument.id,
                                                  'fileName': params.oldDocumentName});

          context.commit("attachDocument",params.newDocument); // Update state
          context.commit("updateDocumentName",params.oldDocumentName);
        }
        catch(error) {
          logger.error(error);
          // If rename fails, project continues with the temp document name.
          context.dispatch("getProject");
        }
      }
      // Reset the annotations as the document has changed.
      context.commit("setAnnotations",[]);
    },

    /**
     * Marks task as "In-Progress".
     *
     * The "Available" tasks will turn to "In-progress" state when end-user navigates to SIGN-view.
     * This is simply to signal that the work on the Task has started.
     *
     * @see FLOW-2576
     * @see SOFLOW-183
     */
    async markTaskInProgress(context, taskId) {
      let url = '/v1/tasks/in-progress';

      try {
        let params = {
          taskId : taskId
        };
        const result = await apiCall.put(url, params);
        logger.debug("markTaskInProgress succeeded:", result);

        let mutationParams = {
          taskId: taskId,
          taskStatus: "IN_PROGRESS"
        };

        context.commit("updateTaskStatus",mutationParams); // Update state.

        return result;
      }
      catch(error) {
        logger.error("markTaskInProgress failed:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },

    /**
     * Get validation result for project document.
     *
     * Expected parameter:
     *
     * params = {
     *   documentId: String,
     * }
     */
    async getDocumentValidationResult(context, documentId) {
      let url = '/v1/documents/project/validate/' + documentId;

      try {
        const result = await apiCall.post(url);
        logger.debug("Retrieved document validation report:", result);
        return result.data;
      }
      catch(error) {
        logger.error("Failed to retrieve document validation report:", error);
        throw error;
      }
    },

    async updatePhase(context, params) {
      let url = '/v1/phases/update';

      try {
        const result = await apiCall.put(url, params);
        logger.debug("Phase updated:", result);
        return result.data;
      }
      catch(error) {
        logger.error("Failed to update phase:", error);
        throw error;
      }
    },

    /**
     * Resolves a Task "rejection".
     *
     * Expected parameters:
     *
     * params = {
     *   "taskId": String,
     *   "message": String,
     *   "resolvationStrategy": String (see tasks.js#RESOLVATION_STRATEGY),
     *   "collaboratorIds": Array,
     *   "documentId": String,
     *   "stepId": String
     * }
     *
     * Do note that collaboratorId, documentId and stepId are used/required
     * only if the RESOLVATION_STRATEGY requires it.
     *
     * More information can be found in DOS-730.
     *
     * @returns {Promise} promise
     */
    async resolveTaskRejection(context, params) {
      let url = 'v1/tasks/resolve/task';

      try {
        const result = await apiCall.put(url, params);
        logger.debug("resolveTaskRejection succeeded:", result);
      }
      catch(error) {
        logger.error("resolveTaskRejection failed:", error);
        logger.error(error.response.data);
        logger.error(error.response.status);
        logger.error(error.response.headers);
        throw error;
      }
    },
  }
}
